import { useState, useEffect } from 'react';
import { useApi } from '@/api/useApi.tsx';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import clsx from 'clsx';
import { Modal } from '@/components';
import { useSizes } from '@/utility/responsive.ts';

interface ContractResponse {
	id?: number;
	name: string;
	description: string;
	contractName: string;
	contractLink: string;
}

interface ContractData {
	id: number;
	name: string;
	description: string;
	contractName: string;
	contractLink: string;
}

interface UseContractModalReturn {
	isModalOpen: boolean;
	isLoading: boolean;
	contractContent: ContractData | null;
	onContractClick: (contract: string) => void;
	closeModal: () => void;
	ContractModal: () => JSX.Element;
}

export const useContractModal = (): UseContractModalReturn => {
	const [activeContract, setActiveContract] = useState<string | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [contractContent, setContractContent] = useState<ContractData | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [contractCache, setContractCache] = useState<Record<string, ContractData>>({});

	const { ContractControllerApiApplicant } = useApi();

	const mapResponseToContractData = (response: ContractResponse): ContractData => {
		return {
			id: response.id || 0,
			name: response.name,
			description: response.description,
			contractName: response.contractName,
			contractLink: response.contractLink
		};
	};

	useEffect(() => {
		if (isModalOpen && activeContract) {
			if (contractCache[activeContract]) {
				setContractContent(contractCache[activeContract]);
				setIsLoading(false);
			} else {
				setIsLoading(true);
			}
		}
	}, [isModalOpen, activeContract, contractCache]);

	useQueryProvider([QUERY_KEYS.findContractApplicant, activeContract], () => ContractControllerApiApplicant.findContract(activeContract), {
		enabled: !!activeContract && isModalOpen && !contractCache[activeContract],
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false,
		onSuccess: data => {
			if (data) {
				const contractData = mapResponseToContractData(data);
				setContractContent(contractData);
				setContractCache(prev => ({
					...prev,
					[activeContract as string]: contractData
				}));
				setIsLoading(false);
			}
		},
		onError: () => {
			setIsLoading(false);
		}
	});

	const onContractClick = (contract: string) => {
		setActiveContract(contract);
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
	};

	const { isMobile } = useSizes();

	const ContractModal = () => (
		<Modal
			onClose={closeModal}
			size="lg"
			contentClassName="contract-modal-container"
			closeOnClickOutside
			showCloseButton
			variant={isMobile ? 'bottom' : 'centered'}
			isOpen={isModalOpen}>
			{isLoading ? (
				<div className="loading-contract">Sözleşme yükleniyor...</div>
			) : contractContent ? (
				<div className="contract-modal-content">
					{contractContent.contractLink && (
						<>
							<div className={clsx('contract-iframe-container full-container')}>
								<iframe
									src={contractContent.contractLink}
									title={contractContent.name}
								/>
							</div>
						</>
					)}
				</div>
			) : (
				<div className="loading-contract">Sözleşme bulunamadı</div>
			)}
		</Modal>
	);

	return {
		isModalOpen,
		isLoading,
		contractContent,
		onContractClick,
		closeModal,
		ContractModal
	};
};
